/**
 * @file 参考書ルートを取得する hook。
 *
 * @what: familyId ごとに1回読む。空配列は「まだ何も取り込んでいない」を表す。
 * @why: ルートは家族の持ち物で学習者に従属しないため、plan の draft 機構とは独立に持つ
 *   (よむ の候補一覧と同じ形)。
 * @failure: 失敗は `error` にする (画面は1行で知らせ、詳細は console にだけ出す)。backend が
 *   未デプロイの間は not_found (404) になるので、ここは「読み込めなかった」に倒して画面を壊さない。
 */

import { useEffect, useState } from "react";
import { fetchTextbookRoutes, type StudyTextbookRoute } from "../api/textbookRoutes";

export type TextbookRoutesState =
  | { status: "loading" }
  | { status: "ready"; routes: StudyTextbookRoute[] }
  | { status: "error" };

export function useTextbookRoutes(familyId: string, enabled: boolean): TextbookRoutesState {
  const [state, setState] = useState<TextbookRoutesState>({ status: "loading" });

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchTextbookRoutes(familyId, controller.signal)
      .then((routes) => {
        if (!controller.signal.aborted) setState({ status: "ready", routes });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load textbook routes", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, enabled]);

  return state;
}
